import type { OptionType } from "components/inputs";
import {
  ControlledTextArea,
  DatePicker,
  Select,
  STRING_TO_DATE,
  TextArea,
} from "components/inputs";
import { format } from "date-fns";
import type { Control, FieldErrors } from "react-hook-form";
import { Controller } from "react-hook-form";

import type { TaskFormData, TaskTypeOption } from "../../types/taskModal.types";

type TaskFormProps = {
  control: Control<TaskFormData>;
  errors: FieldErrors<TaskFormData>;
  assigneeOptions: OptionType[];
  assigneeLabel: string;
  showTaskTypeSelect?: boolean;
  taskTypeOptions?: TaskTypeOption[];
  taskTypeRequired?: boolean;
  getTaskTypeValue?: (value: string) => TaskTypeOption | undefined;
  onTaskTypeChange?: (
    option: unknown,
    onChange: (value: string) => void,
  ) => void;
  showServiceSelect?: boolean;
  serviceOptions?: OptionType[];
  serviceReadOnly?: boolean;
  getServiceValue?: (value: TaskFormData["service"]) => OptionType | undefined;
  messageMode?: "controlled" | "plain";
  messageReadOnly?: boolean;
  messageAdvancedEdit?: boolean;
  messageRequired?: boolean;
};

/* render shared task modal form fields for create/edit and send-to-ops flows. */
const TaskForm = ({
  control,
  errors,
  assigneeOptions,
  assigneeLabel,
  showTaskTypeSelect = false,
  taskTypeOptions = [],
  taskTypeRequired = false,
  getTaskTypeValue,
  onTaskTypeChange,
  showServiceSelect = false,
  serviceOptions = [],
  serviceReadOnly = false,
  getServiceValue,
  messageMode = "controlled",
  messageReadOnly = false,
  messageAdvancedEdit = true,
  messageRequired = false,
}: TaskFormProps) => {
  return (
    <>
      {showServiceSelect && (
        <Controller
          control={control}
          name="service"
          render={({ field: { name, onChange, value } }) => (
            <Select
              name={name}
              placeholder="Select Service"
              label="Service"
              options={serviceOptions}
              errors={errors}
              onChange={onChange}
              readOnly={serviceReadOnly}
              value={getServiceValue?.(value)}
            />
          )}
        />
      )}

      {showTaskTypeSelect && (
        <Controller
          name="type"
          control={control}
          rules={
            taskTypeRequired ? { required: "Task Type is required" } : undefined
          }
          render={({ field: { name, onChange, value } }) => (
            <Select
              name={name}
              label="Task Type"
              options={taskTypeOptions}
              errors={errors}
              onChange={(option) => {
                if (onTaskTypeChange) {
                  onTaskTypeChange(option, onChange);
                  return;
                }

                onChange(option);
              }}
              value={getTaskTypeValue ? getTaskTypeValue(value) : undefined}
            />
          )}
        />
      )}

      <Controller
        name="assignee"
        control={control}
        rules={{ required: "Assignee is required" }}
        render={({ field: { name, onChange, value } }) => (
          <Select
            name={name}
            label={assigneeLabel}
            options={assigneeOptions}
            errors={errors}
            onChange={onChange}
            value={assigneeOptions.find((option) => option.value === value)}
          />
        )}
      />

      <Controller
        name="due_date"
        control={control}
        rules={{ required: "Due Date is required" }}
        render={({ field: { name, onChange, value } }) => (
          <DatePicker
            name={name}
            label="Due Date"
            initialDate={STRING_TO_DATE(value)}
            allowFutureDates={true}
            onDateChange={(date) => {
              const formattedDate = date ? format(date, "MM/dd/yyyy") : null;
              onChange(formattedDate);
            }}
            errors={errors}
            readOnly={false}
            required={true}
          />
        )}
      />

      {messageMode === "plain" ? (
        <Controller
          name="message"
          control={control}
          rules={
            messageRequired ? { required: "Message is required" } : undefined
          }
          render={({ field }) => (
            <TextArea {...field} label="Message" errors={errors} />
          )}
        />
      ) : (
        <ControlledTextArea
          name="message"
          control={control}
          errors={errors}
          readOnly={messageReadOnly}
          label="Message"
          advancedEdit={messageAdvancedEdit}
          onSave={async () => Promise.resolve()}
        />
      )}
    </>
  );
};

export default TaskForm;
